import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useRepoMarginCallHistory } from '@/hooks/trades/use-repo-margin-call-history';
import { useRepoMarginCallBalance } from '@/hooks/trades/use-repo-margin-call-balance';
import { formatCurrencyCode } from '@/utils/trades/mtm-calculations';
import {
  displayCashPool,
  cashPoolHolderCaption,
  bookBalanceCurrency,
  differsFromLastCall,
  LAST_CALL_MISMATCH,
} from '@/utils/trades/cash-pool-display';
import { ReasonBadge } from '@/components/ui/ReasonBadge';
import { RepoCashPoolLedger } from './RepoCashPoolLedger';

const { Panel: DialogPanel, Title: DialogTitle } = Dialog;
const { Child: TransitionChild } = Transition;

// Matches the leave duration on the panel TransitionChild below — keeps the drawer painted while
// it slides out instead of blanking the instant `counterpartyName` clears (same as MtmDetailsDrawer).
const LEAVE_TRANSITION_MS = 250;

interface CashPoolHistoryDrawerProps {
  /** The counterparty whose cash-pool postings to show; null closes the drawer. */
  counterpartyName: string | null;
  /** ISO currency of the pool (matches the bar's repo base currency). */
  currency: string;
  /** LR-577: the MTM counterparty scope stamped on the breakdown's Trade ID links, so the trade
   *  page's back control returns to the scoped MTM view. `undefined` ⇒ unscoped MTM, which resolves
   *  to the first tab — the honest fallback (LR-478). Required, not optional: the value may be
   *  absent but the WIRING may not, and a dropped `cp={urlCp}` would still compile. */
  cp: number | undefined;
  onClose: () => void;
}

/** Balance caption, billing-signed (LR-542 / feedback #63): − = we hold the CP's cash. */
function balanceCaption(
  stored: string | null | undefined,
  currency: string
): string {
  if (stored == null) return '—';
  const v = displayCashPool(Number(stored));
  return `${formatCurrencyCode(v, currency)} (${cashPoolHolderCaption(v)})`;
}

/**
 * LR-479 (variant A): right-side drawer showing the repo cash-pool posting history for one
 * counterparty — clicking the "Cash pool" cell in the MTM bar opens it. Reuses the existing
 * MtmDetailsDrawer slide-over pattern and the shared RepoCashPoolLedger. Read-only.
 */
export const CashPoolHistoryDrawer: React.FC<CashPoolHistoryDrawerProps> = ({
  counterpartyName,
  currency,
  cp,
  onClose,
}) => {
  const closeRef = useRef<HTMLButtonElement>(null);
  // Mirror the prop in local state so the header/ledger stay painted through the leave transition.
  const [displayCp, setDisplayCp] = useState<string | null>(counterpartyName);
  useEffect(() => {
    if (counterpartyName) {
      setDisplayCp(counterpartyName);
      return;
    }
    const timer = setTimeout(() => setDisplayCp(null), LEAVE_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [counterpartyName]);

  // Every call of the counterparty's repo book, whatever currency each was posted in: the book
  // and the balance in the header span them all.
  const {
    data: calls,
    isLoading,
    error,
  } = useRepoMarginCallHistory(displayCp, undefined, displayCp != null);
  // The header reads the pool ledger, the figure the bar's cell and the next call net against;
  // the newest row's `cashpool_after` is only what that call left, shown as such.
  const {
    data: book,
    isLoading: bookLoading,
    error: bookError,
  } = useRepoMarginCallBalance(displayCp ?? undefined, displayCp != null);
  const atLastCall = calls?.[0]?.cashpool_after;
  // Label in the canonical upper ISO the backend stores/filters by. The pool (cashpool_after) is
  // denominated in the call's settlement currency (blank on a legacy row ⇒ its `currency`), falling
  // back to the prop - the GMRA agreement currency.
  const displayCurrency = (
    calls?.[0]?.settlement_currency || calls?.[0]?.currency || currency
  ).toUpperCase();
  const bookCcy = bookBalanceCurrency(book?.pools ?? [], book?.currency || displayCurrency);

  return (
    <Transition show={counterpartyName != null} as={Fragment}>
      {/* The header's badge is focusable and comes first — without this the drawer would open on it. */}
      <Dialog as="div" className="relative z-[100]" onClose={onClose} initialFocus={closeRef}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20" aria-hidden="true" />
        </TransitionChild>

        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <TransitionChild
            as={Fragment}
            enter="transform transition ease-out duration-250"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="flex h-full w-screen max-w-4xl flex-col bg-white shadow-2xl">
              <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
                <div>
                  <DialogTitle
                    as="h2"
                    className="text-base font-semibold text-gray-900"
                  >
                    Cash pool — {displayCp}
                  </DialogTitle>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Book balance:{' '}
                    <span className="font-mono font-medium text-gray-700">
                      {bookError && !book
                        ? '— (ledger unavailable)'
                        : bookLoading
                          ? '…'
                          : bookCcy === null
                            ? '— (held in several currencies, no single total)'
                            : balanceCaption(book?.cashpool, bookCcy)}
                    </span>
                    {bookCcy !== null &&
                      atLastCall != null &&
                      differsFromLastCall(book?.cashpool, atLastCall) && (
                        // leading-3: in running text, see ReasonBadge.
                        <ReasonBadge
                          label={LAST_CALL_MISMATCH.label}
                          reason={LAST_CALL_MISMATCH.reason}
                          className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold leading-3 text-amber-700"
                        />
                      )}
                    {atLastCall != null && (
                      <>
                        <span className="mx-1.5 text-gray-300">·</span>
                        <span>
                          at last call{' '}
                          <span className="font-mono">
                            {formatCurrencyCode(
                              displayCashPool(Number(atLastCall)),
                              displayCurrency
                            )}
                          </span>
                        </span>
                      </>
                    )}
                  </p>
                </div>
                <button
                  ref={closeRef}
                  type="button"
                  onClick={onClose}
                  className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  aria-label="Close cash pool history"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {isLoading && (
                  <div className="space-y-3 p-5" aria-hidden="true">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        key={i}
                        className="animate-pulse rounded border border-gray-100 p-3"
                      >
                        <div className="h-3 w-24 rounded bg-gray-200" />
                        <div className="mt-2 h-3 w-40 rounded bg-gray-200" />
                      </div>
                    ))}
                  </div>
                )}

                {error && (
                  <div className="m-5 flex items-start gap-2 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-red-400" />
                    <div>
                      <p className="font-medium">
                        Failed to load cash pool history
                      </p>
                      <p className="mt-0.5 text-xs text-red-600">
                        {error instanceof Error
                          ? error.message
                          : 'Unknown error'}
                      </p>
                    </div>
                  </div>
                )}

                {!isLoading && !error && calls && calls.length === 0 && (
                  <div className="flex flex-col items-center gap-2 px-5 py-16 text-center">
                    <InformationCircleIcon className="h-8 w-8 text-gray-300" />
                    <p className="text-sm font-medium text-gray-600">
                      No postings yet
                    </p>
                    <p className="text-xs text-gray-400">
                      No repo margin call has been posted for this counterparty.
                    </p>
                  </div>
                )}

                {!isLoading && !error && calls && calls.length > 0 && (
                  <RepoCashPoolLedger calls={calls} origin={{ from: 'mtm', cp }} />
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
};
